// src/page/TicketEdit.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import Select from 'react-select';

// options
import { USAGE_TYPE_OPTIONS, TICKET_PACKAGE_CATEGORY_OPTIONS, TICKET_LEVEL_OPTIONS } from '../constants/ticketOptions';

export default function TicketEdit() {
    useAuthGuard();
    const navigate = useNavigate();
    const { packageId } = useParams();

    // ---------- helpers ----------
    const pad = (n) => String(n).padStart(2, '0');
    const fmtDate = (d) =>
        `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    const fmt = (s) => {
        if (!s) return '';
        const d = new Date(s);
        if (isNaN(d)) return '';
        return fmtDate(d);
    };
    const parseRangeStr = (rangeStr) => {
        if (!rangeStr || !rangeStr.includes('~')) return [null, null];
        const [a, b] = rangeStr.split('~').map(s => s.trim());
        return [a || null, b || null];
    };
    const parseDateTime = (str) => {
        if (!str) return null;
        const [datePart, timePart] = str.trim().split(' ');
        if (!datePart || !timePart) return null;
        const [yyyy, mm, dd] = datePart.split('/').map(Number);
        const [HH, II] = timePart.split(':').map(Number);
        const d = new Date(yyyy, (mm || 1) - 1, dd || 1, HH || 0, II || 0, 0, 0);
        return isNaN(d) ? null : d;
    };
    const daysBetween = (a, b) => {
        if (!a || !b) return null;
        const ms = Math.abs(b.getTime() - a.getTime());
        const days = Math.round(ms / (1000 * 60 * 60 * 24));
        return Math.max(1, days || 1);
    };

    // validation helpers
    const RANGE_RE = /^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2} ~ \d{4}\/\d{2}\/\d2 \d{2}:\d{2}$/;
    const rangeError = (label, val) => {
        if (!val) return `${label}は必須です`;
        if (!/^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2} ~ \d{4}\/\d{2}\/\d{2} \d{2}:\d{2}$/.test(val)) {
            return `${label}の形式が正しくありません（YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm）`;
        }
        const [a, b] = parseRangeStr(val);
        const d1 = parseDateTime(a), d2 = parseDateTime(b);
        if (!d1 || !d2) return `${label}の形式が正しくありません（YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm）`;
        if (d1 > d2) return `${label}は開始≦終了にしてください`;
        return null;
    };

    // ---------- pickers refs ----------
    const validFpRef = useRef(null);
    const salesFpRef = useRef(null);
    const publishFpRef = useRef(null);

    // ---------- main states ----------
    const [packageName, setPackageName] = useState('');
    const [ticketBland, setTicketBland] = useState('');
    const [usageType, setUsageType] = useState(USAGE_TYPE_OPTIONS[0][0]); // now driven by voucher
    const [ticketPackageCategory, setTicketPackageCategory] = useState('0'); // kept for payload compatibility
    const [ticketLevel, setTicketLevel] = useState('0');
    const [maxHoldingNumber, setMaxHoldingNumber] = useState('');


    const [howToUse, setHowToUse] = useState('');
    const [termsOfUse, setTermsOfUse] = useState('');
    const [caution, setCaution] = useState('');

    // N日券
    const [ticketInterval, setTicketInterval] = useState(''); // = t_ticket_voucher.effective_days

    // 有効期間（voucher由来）
    const [validRange, setValidRange] = useState('');

    // 有効時刻（voucher由来）— 4桁（2600なども許容）
    const [validTime, setValidTime] = useState('2359'); // = t_ticket_voucher.effective_time
    const [voucherId, setVoucherId] = useState('');     // 更新用に保持

    // 右カラム
    const [publishedFlag, setPublishedFlag] = useState('0');
    const [salesRange, setSalesRange] = useState('');
    const [publishRange, setPublishRange] = useState('');
    const [displayOrder, setDisplayOrder] = useState('');

    // price/fee/user cancel
    const [pricePackageAdult, setPricePackageAdult] = useState('');
    const [pricePackageChild, setPricePackageChild] = useState('');
    const [refundFeePerUser, setRefundFeePerUser] = useState('');
    const [cancellationFeePerUser, setCancellationFeePerUser] = useState('');
    const [userCancelable, setUserCancelable] = useState('0');

    // spot select
    const [spotId, setSpotId] = useState('');
    const [spots, setSpots] = useState([]);
    const [loadingSpots, setLoadingSpots] = useState(false);

    const spotOptions = useMemo(
        () => spots.map(s => ({ value: s.spotId, label: s.spotName })),
        [spots]
    );

    // image upload (single)
    const [imageFile, setImageFile] = useState(null);        // File | null
    const [imagePreview, setImagePreview] = useState('');    // object URL or server/absolute URL
    const [serverImagePath, setServerImagePath] = useState(''); // persisted path (DB: ticket_image)
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);

    // meta
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState('');
    const isFree = ticketPackageCategory === '1';

    // --- validation UI ---
    const [errors, setErrors] = useState({});
    const invalid = (k) => (errors[k] ? 'is-invalid' : '');
    const feedback = (k) => (errors[k] ? <div className="invalid-feedback d-block">{errors[k]}</div> : null);
    // 緩和：4桁数字なら許容（26:00 等の運用も想定）
    const isHHmm = (v) => /^\d{4}$/.test(v);
    const scrollToFirstError = (errObj) => {
        const keys = Object.keys(errObj);
        if (!keys.length) return;
        const first = keys[0];
        const node =
            document.querySelector(`[data-error-key="${first}"]`) ||
            document.querySelector(`[name="${first}"]`);
        if (node?.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // ---------- init flatpickr ----------
    useEffect(() => {
        if (!window.flatpickr) return;

        validFpRef.current = window.flatpickr('#valid_period_range', {
            mode: 'range',
            enableTime: true,
            time_24hr: true,
            dateFormat: 'Y/m/d H:i',
            allowInput: false,
            clickOpens: false,
            locale: window.flatpickr.l10ns?.ja || 'ja',
            onChange: (selectedDates) => {
                if (selectedDates.length === 2) setValidRange(`${fmtDate(selectedDates[0])} ~ ${fmtDate(selectedDates[1])}`);
                if (selectedDates.length === 0) setValidRange('');
            }
        });

        salesFpRef.current = window.flatpickr('#sales_range', {
            mode: 'range',
            enableTime: true,
            time_24hr: true,
            dateFormat: 'Y/m/d H:i',
            allowInput: false,
            clickOpens: false,
            locale: window.flatpickr.l10ns?.ja || 'ja',
            onChange: (selectedDates) => {
                if (selectedDates.length === 2) setSalesRange(`${fmtDate(selectedDates[0])} ~ ${fmtDate(selectedDates[1])}`);
                if (selectedDates.length === 0) setSalesRange('');
            }
        });

        publishFpRef.current = window.flatpickr('#publish_range', {
            mode: 'range',
            enableTime: true,
            time_24hr: true,
            dateFormat: 'Y/m/d H:i',
            allowInput: false,
            locale: window.flatpickr.l10ns?.ja || 'ja',
            onChange: (selectedDates) => {
                if (selectedDates.length === 2) setPublishRange(`${fmtDate(selectedDates[0])} ~ ${fmtDate(selectedDates[1])}`);
                if (selectedDates.length === 0) setPublishRange('');
            }
        });

        return () => {
            try { validFpRef.current?.destroy(); } catch { }
            try { salesFpRef.current?.destroy(); } catch { }
            try { publishFpRef.current?.destroy(); } catch { }
        };
    }, []);

    // reflect controlled -> picker (and clear when empty)
    useEffect(() => {
        if (!validFpRef.current) return;
        if (validRange && validRange.includes('~')) {
            const [a, b] = parseRangeStr(validRange);
            if (a && b) validFpRef.current.setDate([a, b], true);
        } else {
            try { validFpRef.current.clear(); } catch { }
        }
    }, [validRange]);

    useEffect(() => {
        if (!salesFpRef.current) return;
        if (salesRange && salesRange.includes('~')) {
            const [a, b] = parseRangeStr(salesRange);
            if (a && b) salesFpRef.current.setDate([a, b], true);
        } else {
            try { salesFpRef.current.clear(); } catch { }
        }
    }, [salesRange]);

    useEffect(() => {
        if (!publishFpRef.current) return;
        if (publishRange && publishRange.includes('~')) {
            const [a, b] = parseRangeStr(publishRange);
            if (a && b) publishFpRef.current.setDate([a, b], true);
        } else {
            try { publishFpRef.current.clear(); } catch { }
        }
    }, [publishRange]);

    // explicit clear handlers
    const clearValid = () => { setValidRange(''); try { validFpRef.current?.clear(); } catch { } };
    const clearSales = () => { setSalesRange(''); try { salesFpRef.current?.clear(); } catch { } };
    const clearPublish = () => { setPublishRange(''); try { publishFpRef.current?.clear(); } catch { } };

    // ---------- file URL helpers ----------
    const fileURL = (p) => (p ? `${API_BASE_URL.replace(/\/$/, '')}/files/${encodeURIComponent(p)}` : '');
    const isBlobUrl = (u) => {
        try { return new URL(u).protocol === 'blob:'; } catch { return false; }
    };
    const isAbsoluteUrl = (u) => /^https?:\/\//i.test(u || '');

    // ---------- image helpers ----------
    const fileInputRef = useRef(null);

    const setSingleImage = (file) => {
        if (!file) return;
        if (!file.type?.startsWith('image/')) {
            alert('画像ファイルを選択してください');
            return;
        }
        if (imagePreview && isBlobUrl(imagePreview)) URL.revokeObjectURL(imagePreview);
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handlePickClick = () => fileInputRef.current?.click();

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) setSingleImage(file);
        e.target.value = '';
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) setSingleImage(file);
    };

    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
    const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); };

    const clearImage = () => {
        if (imagePreview && isBlobUrl(imagePreview)) URL.revokeObjectURL(imagePreview);
        setImageFile(null);
        setImagePreview('');
        setServerImagePath(''); // explicit remove
    };

    useEffect(() => {
        return () => { if (imagePreview && isBlobUrl(imagePreview)) URL.revokeObjectURL(imagePreview); };
    }, [imagePreview]);

    // ---------- load detail ----------
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setLoading(true);
                setError('');
                const token = localStorage.getItem('access_token');
                const res = await fetch(`${API_BASE_URL}/v1/ticket/${packageId}`, {
                    headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
                    signal: ac.signal
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                const pkg = data?.package || {};
                const vch = Array.isArray(data?.vouchers) && data.vouchers.length ? data.vouchers[0] : null;

                // basic
                setPackageName(pkg.packageName || '');
                setTicketBland(pkg.ticketBrand || '');
                setTicketPackageCategory((pkg.ticketPackageCategory ?? '0') + '');
                setTicketLevel(((pkg.ticketLevel ?? 0)) + '');
                setMaxHoldingNumber((pkg.maxHoldingNumber ?? '') + '');
                setHowToUse(pkg.howToUse || '');
                setTermsOfUse(pkg.termsOfUse || '');
                setCaution(pkg.caution || '');

                // spot
                setSpotId(pkg.spotId || '');

                // validity (voucher source)
                if (vch) {
                    setVoucherId(vch.voucherId || '');
                    // usageType is stored in voucher
                    setUsageType(
                        vch.usageType != null && vch.usageType !== ''
                            ? String(vch.usageType)
                            : USAGE_TYPE_OPTIONS[0][0]
                    );
                    const vs = fmt(vch.effectivePeriodStartDate);
                    const ve = fmt(vch.effectivePeriodEndDate);
                    setValidRange(vs && ve ? `${vs} ~ ${ve}` : '');

                    const et = (vch.effectiveTime ?? '').toString().replace(/\D/g, '').slice(0, 4);
                    if (et) setValidTime(et.padStart(4, '0'));

                    if (vch.effectiveDays != null) {
                        setTicketInterval(String(vch.effectiveDays));
                    } else if (vch.effectivePeriodStartDate && vch.effectivePeriodEndDate) {
                        const d1 = new Date(vch.effectivePeriodStartDate);
                        const d2 = new Date(vch.effectivePeriodEndDate);
                        const dd = daysBetween(d1, d2);
                        if (dd) setTicketInterval(String(dd));
                    }
                } else {
                    const vs = fmt(pkg.validStartDate);
                    const ve = fmt(pkg.validEndDate);
                    setValidRange(vs && ve ? `${vs} ~ ${ve}` : '');
                    setUsageType(USAGE_TYPE_OPTIONS[0][0]);
                }

                // right column
                setPublishedFlag(pkg.publishedFlag ? '1' : '0');
                const ss = fmt(pkg.salesStartDate);
                const se = fmt(pkg.salesEndDate);
                setSalesRange(ss && se ? `${ss} ~ ${se}` : '');
                const ps = fmt(pkg.publishedStartDate);
                const pe = fmt(pkg.publishedEndDate);
                setPublishRange(ps && pe ? `${ps} ~ ${pe}` : '');
                setDisplayOrder((pkg.displayOrder ?? '') + '');

                // prices/fees
                setPricePackageAdult((pkg.pricePackageAdult ?? '') + '');
                setPricePackageChild((pkg.pricePackageChild ?? '') + '');
                setRefundFeePerUser((pkg.refundFeePerUser ?? '') + '');
                setCancellationFeePerUser((pkg.cancellationFeePerUser ?? '') + '');
                setUserCancelable(pkg.userCancelable ? '1' : '0');

                // preset server-stored image (DB column: ticket_image)
                const persisted = pkg.ticketImagePath || pkg.ticketImage || '';
                setServerImagePath(persisted);
                if (persisted) {
                    setImagePreview(isAbsoluteUrl(persisted) ? persisted : fileURL(persisted));
                } else {
                    setImagePreview('');
                }
            } catch (e) {
                if (e.name !== 'AbortError') setError('読み込みに失敗しました');
            } finally {
                setLoading(false);
            }
        })();
        return () => ac.abort();
    }, [packageId]);

    // スポット一覧取得
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setLoadingSpots(true);
                const token = localStorage.getItem('access_token');
                const res = await fetch(`${API_BASE_URL}/v1/spot?page=1&size=200`, {
                    headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
                    signal: ac.signal
                });
                const data = await res.json().catch(() => ({}));
                const list = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
                const mapped = list.map(s => ({
                    spotId: s.spotId ?? s.id ?? s.spot_id ?? '',
                    spotName: s.spotName ?? s.name ?? s.spot_name ?? ''
                })).filter(x => x.spotId && x.spotName);
                setSpots(mapped);
            } catch (_) {
                setSpots([]);
            } finally {
                setLoadingSpots(false);
            }
        })();
        return () => ac.abort();
    }, []);

    // ---------- validation ----------
    const validate = () => {
        const err = {};
        if (!packageName.trim()) err.packageName = '乗車券名は必須です';
        if (!isHHmm(validTime)) err.validTime = '有効時刻は4桁のHHmmで入力してください（例: 2359, 2600 など）';

        const vr = rangeError('有効期間', validRange);
        if (vr) err.validRange = vr;

        const se = rangeError('販売期間', salesRange);
        if (se) err.salesRange = se;

        const pe = rangeError('公開期間', publishRange);
        if (pe) err.publishRange = pe;

        setErrors(err);
        return err;
    };

    // helpers for payload emission
    const strToDB = (s) => (s == null ? '' : String(s));
    const intToDB = (s) => {
        if (s === '' || s == null) return null;
        const v = parseInt(s, 10);
        return Number.isFinite(v) ? v : null;
    };

    // ---- upload image to /v1/file (path=ticket/<packageId>/) ----
    const uploadImageIfNeeded = async () => {
        if (!imageFile) return null;
        setUploading(true);
        try {
            const token = localStorage.getItem('access_token');
            const fd = new FormData();
            fd.append('file', imageFile, imageFile.name);
            fd.append('path', `ticket/${packageId}/`);

            const res = await fetch(`${API_BASE_URL}/v1/file`, {
                method: 'POST',
                headers: { Authorization: token ? `Bearer ${token}` : '' },
                body: fd
            });

            if (!res.ok) {
                let msg = `画像アップロードに失敗しました (HTTP ${res.status})`;
                try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
                throw new Error(msg);
            }

            let resultPath = null;
            try {
                const j = await res.json();
                resultPath = j?.path || j?.result || null;
            } catch {
                resultPath = await res.text();
            }
            return resultPath;
        } finally {
            setUploading(false);
        }
    };

    // ---------- submit (PUT) ----------
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;

        const err = validate();
        if (Object.keys(err).length) {
            scrollToFirstError(err);
            return;
        }

        // parse period once
        let vStartISO = null, vEndISO = null, effDays = intToDB(ticketInterval);
        if (validRange.includes('~')) {
            const [a, b] = parseRangeStr(validRange);
            const d1 = parseDateTime(a), d2 = parseDateTime(b);
            if (d1 && d2) {
                vStartISO = d1.toISOString();
                vEndISO = d2.toISOString();
                if (effDays == null) {
                    const dd = daysBetween(d1, d2);
                    if (dd) effDays = dd;
                }
            }
        }

        try {
            setSaving(true);

            // 1) Upload image first (if any)
            let uploadedImagePath = null;
            if (imageFile) {
                uploadedImagePath = await uploadImageIfNeeded();
                if (!uploadedImagePath) {
                    throw new Error('画像のアップロードに失敗しました。');
                }
            }

            // 2) Build payload with final image path
            const [sA, sB] = (salesRange || '').split('~').map(s => s?.trim());
            const [pA, pB] = (publishRange || '').split('~').map(s => s?.trim());

            const payload = {
                packageName: packageName.trim(),
                spotId: spotId || undefined,
                ticketPackageCategory: ticketPackageCategory || undefined,
                ticketLevel: intToDB(ticketLevel),
                maxHoldingNumber: intToDB(maxHoldingNumber),
                displayOrder: intToDB(displayOrder),
                howToUse: strToDB(howToUse),
                termsOfUse: strToDB(termsOfUse),
                caution: strToDB(caution),
                pricePackageAdult: intToDB(pricePackageAdult),
                pricePackageChild: intToDB(pricePackageChild),
                refundFeePerUser: intToDB(refundFeePerUser),
                cancellationFeePerUser: intToDB(cancellationFeePerUser),
                userCancelable: userCancelable === '1',
                publishedFlag: publishedFlag === '1',

                ticketBrand: strToDB(ticketBland),

                salesStartDate: sA ? parseDateTime(sA)?.toISOString() : null,
                salesEndDate: sB ? parseDateTime(sB)?.toISOString() : null,
                publishedStartDate: pA ? parseDateTime(pA)?.toISOString() : null,
                publishedEndDate: pB ? parseDateTime(pB)?.toISOString() : null,

                vouchers: [{
                    voucherId: voucherId || undefined,
                    usageType: usageType || undefined,
                    effectiveTime: strToDB(validTime),
                    effectivePeriodStartDate: vStartISO,
                    effectivePeriodEndDate: vEndISO,
                    effectiveDays: effDays
                }],

                ticketImage: uploadedImagePath ?? serverImagePath ?? ''
            };

            const token = localStorage.getItem('access_token');
            const res = await fetch(`${API_BASE_URL}/v1/ticket/${packageId}`, {
                method: 'PUT',
                headers: {
                    Authorization: token ? `Bearer ${token}` : '',
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                let msg = `更新に失敗しました (HTTP ${res.status})`;
                try {
                    const j = await res.json();
                    if (j?.error) msg = j.error;
                } catch { }
                throw new Error(msg);
            }
            alert('更新しました。');
            navigate('/ticket');
        } catch (e2) {
            alert(e2?.message || '更新に失敗しました。');
        } finally {
            setSaving(false);
        }
    };

    // ---------- delete (DELETE) ----------
    const handleDelete = async () => {
        if (deleting) return;
        if (!window.confirm('本当に削除しますか？この操作は取り消せません。')) return;
        try {
            setDeleting(true);
            const token = localStorage.getItem('access_token');
            const res = await fetch(`${API_BASE_URL}/v1/ticket/${packageId}`, {
                method: 'DELETE',
                headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' }
            });
            if (!res.ok) throw new Error(`削除に失敗しました (HTTP ${res.status})`);
            alert('削除しました。');
            navigate('/ticket');
        } catch (e) {
            alert(e.message || '削除に失敗しました。');
        } finally {
            setDeleting(false);
        }
    };

    const savingOrUploading = saving || uploading || deleting;

    return (
        <Layout>
            <>
                <nav className="mb-3" aria-label="breadcrumb">
                    <ol className="breadcrumb mb-0">
                        <li className="breadcrumb-item"><a href="/ticket">乗車券管理</a></li>
                        <li className="breadcrumb-item active">編集</li>
                    </ol>
                </nav>

                <form className="mb-9" onSubmit={handleSubmit}>
                    <div className="row g-3 flex-between-end mb-5">
                        <div className="col-auto">
                            <h2 className="mb-2">乗車券編集</h2>
                        </div>
                        <div className="col-auto">
                            <button className="btn btn-phoenix-secondary me-2 mb-2 mb-sm-0" type="button"
                                onClick={() => window.history.back()} disabled={savingOrUploading}>キャンセル</button>
                            <button className="btn btn-primary me-2 mb-2 mb-sm-0" type="submit" disabled={savingOrUploading}>
                                {uploading ? '画像アップロード中...' : (saving ? '保存中...' : '更新')}
                            </button>
                            <button className="btn btn-danger mb-2 mb-sm-0" type="button"
                                onClick={handleDelete} disabled={savingOrUploading}>
                                {deleting ? '削除中...' : '削除'}
                            </button>
                        </div>
                    </div>

                    <div className="row g-5">
                        {/* left */}
                        <div className="col-12 col-xl-8">

                            {/* ID */}
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">乗車券ID</h4>
                                        <input className="form-control" type="text" value={packageId} disabled />
                                    </div>
                                </div>
                            </div>

                            {/* 基本 */}
                            <div className={`mb-4 ${invalid('packageName')}`} data-error-key="packageName">
                                <h4 className="mb-3">乗車券名<span className="text-danger">*</span></h4>
                                <input
                                    className={`form-control ${invalid('packageName')}`}
                                    type="text"
                                    value={packageName}
                                    onChange={(e) => setPackageName(e.target.value)}
                                    disabled={true}
                                />
                                {feedback('packageName')}
                            </div>

                            <div className="mb-4">
                                <h4 className="mb-3">スポット名<span className="text-danger">*</span></h4>
                                <Select
                                    options={spotOptions}
                                    value={spotOptions.find(o => o.value === spotId) || null}
                                    onChange={(opt) => setSpotId(opt?.value || '')}
                                    isDisabled={true}
                                    isClearable
                                    placeholder={loadingSpots ? '読み込み中…' : '選択または検索'}
                                    classNamePrefix="react-select"
                                    menuPortalTarget={document.body}
                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    filterOption={(opt, input) =>
                                        opt.label.toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">券種</h4>
                                        <div className="input-group">
                                            <input className="form-control" type="text" name="ticket_bland"
                                                value={ticketBland} onChange={(e) => setTicketBland(e.target.value)}
                                                disabled={true} />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">N日券</h4>
                                        <div className="input-group">
                                            <input
                                                className="form-control"
                                                type="number"
                                                min="1"
                                                value={ticketInterval}
                                                onChange={(e) => setTicketInterval(e.target.value)}
                                                disabled={true}
                                            />
                                            <span className="input-group-text">日</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 有効期間 & 有効時刻（voucher由来） */}
                            <div className="row">
                                <div className="col-md-6">
                                    <div className={`mb-4 ${invalid('validRange')}`} data-error-key="validRange">
                                        <h4 className="mb-3">有効期間<span className="text-danger">*</span></h4>
                                        <div className="input-group">
                                            <input
                                                className={`form-control ${invalid('validRange')}`}
                                                type="text"
                                                id="valid_period_range"
                                                placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                                                value={validRange}
                                                readOnly
                                                disabled={true}
                                            />
                                            {/* <span
                                                className="input-group-text"
                                                role="button"
                                                tabIndex={0}
                                                onClick={clearValid}
                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clearValid(); }}
                                                aria-label="クリア"
                                                title="クリア"
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                            >×</span> */}
                                        </div>
                                        {feedback('validRange')}
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className={`mb-4 ${invalid('validTime')}`} data-error-key="validTime">
                                        <h4 className="mb-3">有効時刻<span className="text-danger">*</span></h4>
                                        <input
                                            className={`form-control ${invalid('validTime')}`}
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={4}
                                            placeholder="HHmm 例: 2359 / 2600"
                                            value={validTime}
                                            onChange={(e) => {
                                                const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                                                setValidTime(v);
                                                if (errors.validTime) {
                                                    const next = { ...errors };
                                                    if (isHHmm(v)) delete next.validTime;
                                                    setErrors(next);
                                                }
                                            }}
                                            disabled={savingOrUploading}
                                        />
                                        {feedback('validTime')}
                                    </div>
                                </div>
                            </div>

                            {/* テキスト群 */}
                            <div className="mb-4">
                                <h4 className="mb-3">使用方法</h4>
                                <textarea className="form-control" rows={4}
                                    value={howToUse} onChange={(e) => setHowToUse(e.target.value)}
                                    disabled={savingOrUploading} />
                            </div>
                            <div className="mb-4">
                                <h4 className="mb-3">利用条件</h4>
                                <textarea className="form-control" rows={4}
                                    value={termsOfUse} onChange={(e) => setTermsOfUse(e.target.value)}
                                    disabled={savingOrUploading} />
                            </div>
                            <div className="mb-4">
                                <h4 className="mb-3">注意事項</h4>
                                <textarea className="form-control" rows={4}
                                    value={caution} onChange={(e) => setCaution(e.target.value)}
                                    disabled={savingOrUploading} />
                            </div>

                            {/* 区分/レベル/枚数 */}
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">チケット利用区分</h4>
                                        <div className="d-flex align-items-center gap-3">
                                            {USAGE_TYPE_OPTIONS.map(([v, label]) => (
                                                <label className="mb-0" key={v}>
                                                    <input type="radio" name="usage_type" value={v}
                                                    disabled={true}
                                                        checked={usageType === v} onChange={(e) => setUsageType(e.target.value)} /> {label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-4 col-md-6">
                                    <h4 className="mb-3">乗車券区分</h4>
                                    <div className="d-flex align-items-center gap-3">
                                        {TICKET_PACKAGE_CATEGORY_OPTIONS.map(([v, label]) => (
                                            <label className="mb-0" key={v}>
                                                <input type="radio" name="ticket_price" value={v}
                                                    checked={ticketPackageCategory === v}
                                                    disabled={true}
                                                    onChange={(e) => setTicketPackageCategory(e.target.value)} /> {label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">チケットレベル</h4>
                                        <div className="d-flex align-items-center gap-3">
                                            {TICKET_LEVEL_OPTIONS.map((lv) => (
                                                <label className="mb-0" key={lv}>
                                                    <input type="radio" name="ticket_level" value={lv}
                                                        checked={ticketLevel === lv}
                                                        onChange={(e) => setTicketLevel(e.target.value)}
                                                        disabled={savingOrUploading} /> {lv}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-4 col-md-6">
                                    <h4 className="mb-3">一人当たり保持（購入）可能枚数</h4>
                                    <input className="form-control" type="number" value={maxHoldingNumber}
                                        onChange={(e) => setMaxHoldingNumber(e.target.value)}
                                        disabled={savingOrUploading} />
                                </div>
                            </div>

                            {/* ================= パッケージ販売価格 ================= */}
                            <section className="mt-5">
                                <h2 className="mb-2">パッケージ販売価格</h2>
                                <br />
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <h4 className="mb-3">大人<span className="text-danger">*</span></h4>
                                            <div className="input-group">
                                                <input className="form-control" type="number" min="0"
                                                    value={pricePackageAdult} onChange={(e) => setPricePackageAdult(e.target.value)}
                                                    disabled={true} />
                                                <span className="input-group-text">円</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-4 col-md-6">
                                        <h4 className="mb-3">小人<span className="text-danger">*</span></h4>
                                        <div className="input-group">
                                            <input className="form-control" type="number" min="0"
                                                value={pricePackageChild} onChange={(e) => setPricePackageChild(e.target.value)}
                                                disabled={true} />
                                            <span className="input-group-text">円</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <h4 className="mb-3">払い戻し手数料<span className="text-danger">*</span></h4>
                                            <div className="input-group">
                                                <input className="form-control" type="number" min="0"
                                                    value={refundFeePerUser} onChange={(e) => setRefundFeePerUser(e.target.value)}
                                                    disabled={savingOrUploading || isFree} />
                                                <span className="input-group-text">円</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-4 col-md-6">
                                        <h4 className="mb-3">取り消し手数料<span className="text-danger">*</span></h4>
                                        <div className="input-group">
                                            <input className="form-control" type="number" min="0"
                                                value={cancellationFeePerUser} onChange={(e) => setCancellationFeePerUser(e.target.value)}
                                                disabled={savingOrUploading || isFree} />
                                            <span className="input-group-text">円</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <h4 className="mb-3">ユーザーキャンセルフラグ</h4>
                                            <div className="d-flex align-items-center gap-3">
                                                <label className="mb-0">
                                                    <input type="radio" name="ticket_user_cancel_flag" value="0"
                                                        checked={userCancelable === '0'}
                                                        onChange={() => setUserCancelable('0')}
                                                        disabled={savingOrUploading} /> 否
                                                </label>
                                                <label className="mb-0">
                                                    <input type="radio" name="ticket_user_cancel_flag" value="1"
                                                        checked={userCancelable === '1'}
                                                        onChange={() => setUserCancelable('1')}
                                                        disabled={savingOrUploading} /> 可
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-4 col-md-6">{/* 空き */}</div>
                                </div>
                            </section>

                        </div>

                        {/* right */}
                        <div className="col-12 col-xl-4">
                            <div className="row g-2">
                                <div className="col-12 col-xl-12">
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <div className="row gx-3">
                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className="mb-4">
                                                        <h5 className="mb-3">公開設定<span className="text-danger">*</span></h5>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <label className="mb-0">
                                                                <input type="radio" name="publish_status" value="0"
                                                                    checked={publishedFlag === '0'}
                                                                    onChange={() => setPublishedFlag('0')}
                                                                    disabled={savingOrUploading} /> 非公開
                                                            </label>
                                                            <label className="mb-0">
                                                                <input type="radio" name="publish_status" value="1"
                                                                    checked={publishedFlag === '1'}
                                                                    onChange={() => setPublishedFlag('1')}
                                                                    disabled={savingOrUploading} /> 公開
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className={`col-12 col-sm-6 col-xl-12 ${invalid('publishRange') ? 'is-invalid' : ''}`} data-error-key="publishRange">
                                                    <div className="mb-4">
                                                        <div className="d-flex flex-wrap mb-2">
                                                            <h5 className="mb-0 text-body-highlight me-2">公開期間<span className="text-danger">*</span></h5>
                                                        </div>
                                                        <div className="input-group">
                                                            <input
                                                                className={`form-control ${invalid('publishRange')}`}
                                                                type="text"
                                                                id="publish_range"
                                                                placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                                                                value={publishRange}
                                                                readOnly
                                                            />
                                                            <span
                                                                className="input-group-text"
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={clearPublish}
                                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clearPublish(); }}
                                                                aria-label="クリア"
                                                                title="クリア"
                                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                            >
                                                                ×
                                                            </span>
                                                        </div>
                                                        {feedback('publishRange')}
                                                    </div>
                                                </div>

                                                <div className={`col-12 col-sm-6 col-xl-12 ${invalid('salesRange') ? 'is-invalid' : ''}`} data-error-key="salesRange">
                                                    <div className={`mb-4 ${invalid('salesRange')}`} data-error-key="salesRange">
                                                        <div className="d-flex flex-wrap mb-2">
                                                            <h5 className="mb-0 text-body-highlight me-2">販売期間<span className="text-danger">*</span></h5>
                                                        </div>
                                                        <div className="input-group">
                                                            <input
                                                                className={`form-control ${invalid('salesRange')}`}
                                                                type="text"
                                                                id="sales_range"
                                                                placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                                                                value={salesRange}
                                                                readOnly
                                                                disabled={true}
                                                            />
                                                            {/* <span
                                                                className="input-group-text"
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={clearSales}
                                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clearSales(); }}
                                                                aria-label="クリア"
                                                                title="クリア"
                                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                            >×</span> */}
                                                        </div>
                                                        {feedback('salesRange')}
                                                    </div>
                                                </div>

                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className="mb-4">
                                                        <div className="d-flex flex-wrap mb-2">
                                                            <h5 className="mb-0 text-body-highlight me-2">表示順</h5>
                                                        </div>
                                                        <input className="form-control" type="number"
                                                            value={displayOrder} onChange={(e) => setDisplayOrder(e.target.value)}
                                                            disabled={savingOrUploading} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* image card (single, like SpotEdit) */}
                                <div className="col-12 col-xl-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <h4 className="card-title mb-3">乗車券画像</h4>

                                            {!imagePreview && (
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={handlePickClick}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePickClick(); }}
                                                    onDrop={handleDrop}
                                                    onDragOver={handleDragOver}
                                                    onDragEnter={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                    className={`border border-dashed rounded-3 p-4 text-center ${isDragOver ? 'bg-body-tertiary' : ''}`}
                                                    style={{ cursor: 'pointer' }}
                                                    aria-label="画像をドラッグ&ドロップ、またはクリックして選択"
                                                >
                                                    <p className="mb-2">ここに画像をドラッグ＆ドロップ</p>
                                                    <p className="mb-2">または</p>
                                                    <button type="button" className="btn btn-outline-primary" disabled={savingOrUploading}>ファイルを選択</button>
                                                    <p className="text-body-tertiary mt-2 mb-0">対応形式: 画像ファイル（1枚まで）</p>
                                                </div>
                                            )}

                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*"
                                                onChange={handleFileChange}
                                                style={{ display: 'none' }}
                                                disabled={savingOrUploading}
                                            />

                                            {imagePreview && (
                                                <div className="d-flex align-items-start gap-3">
                                                    <img
                                                        src={imagePreview}
                                                        alt="プレビュー"
                                                        className="rounded border"
                                                        style={{ width: 160, height: 160, objectFit: 'cover' }}
                                                    />
                                                    <div className="flex-grow-1">
                                                        <div className="mb-2 small text-body-secondary">
                                                            {imageFile?.name} {imageFile ? `(${Math.round(imageFile.size / 1024)} KB)` : (serverImagePath ? `(サーバー保存: ${serverImagePath})` : '')}
                                                        </div>
                                                        <div className="d-flex gap-2">
                                                            <button type="button" className="btn btn-outline-secondary" onClick={handlePickClick} disabled={savingOrUploading}>
                                                                画像を変更
                                                            </button>
                                                            <button type="button" className="btn btn-outline-danger" onClick={clearImage} disabled={savingOrUploading}>
                                                                画像を削除
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {loading && <div className="text-body-secondary mt-3">読み込み中...</div>}
                                            {!loading && error && <div className="text-danger mt-3">{error}</div>}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </form>
            </>
        </Layout>
    );
}
